import Component from "@glimmer/component";
import { LinkTo } from "@ember/routing";
import { on } from "@ember/modifier";
import { t } from "ember-intl";
import canManage from "glesys-controlpanel/helpers/can-manage";
import modal from "glesys-controlpanel/helpers/modal";
import dateFormat from "glesys-controlpanel/helpers/date-format";
import PageContent from "glesys-controlpanel/components/page-content";
import Box from "glesys-controlpanel/components/box";
import Table from "glesys-controlpanel/components/table";
import TableTh from "glesys-controlpanel/components/table/th";
import TableTd from "glesys-controlpanel/components/table/td";
import Badge from "glesys-controlpanel/components/badge";
import DataCenter from "glesys-controlpanel/components/data-center";
import IconsStopwatch from "glesys-controlpanel/components/icons/stopwatch";
import IconsCalendar from "glesys-controlpanel/components/icons/calendar";
import Dropdown from "glesys-controlpanel/components/dropdown";
import DropdownDestructiveItem from "glesys-controlpanel/components/dropdown/destructive-item";
import IconsMoreHorizontal from "glesys-controlpanel/components/icons/more-horizontal";
import EntityShipment from "glesys-controlpanel/components/entity/shipment";

export default class RemoteHandsRequestDetails extends Component {
  get remoteHandsRequest() {
    return this.args.model.remoteHandsRequest;
  }

  get heading() {
    return this.remoteHandsRequest.title || this.remoteHandsRequest.id;
  }

  get services() {
    return this.remoteHandsRequest.servicekeys ?? [];
  }

  get colocationRacks() {
    return this.args.model.colocationRacks;
  }

  isColocationServer = (serviceKey) => {
    return serviceKey.startsWith("cs");
  };

  colocationRack = (serviceKey) => {
    return this.colocationRacks.find((colocationRack) => colocationRack.id === serviceKey);
  };

  <template>
    <PageContent>
      <div class="flex items-start justify-between pb-4 border-b border-gray-200">
        <div class="flex flex-col gap-2 min-w-0">
          <h1 class="text-xl text-black flex flex-wrap items-center gap-2">
            <span class="truncate max-w-128" data-test-title title={{this.heading}}>{{this.heading}}</span>
            {{#if this.remoteHandsRequest.title}}
              <span class="shrink-0 text-gray-500" data-test-remote-hands-request-details-key>
                {{this.remoteHandsRequest.id}}
              </span>
            {{/if}}
            <Badge
              data-test-remote-hands-request-details-workflow-state
              @color={{this.remoteHandsRequest.workflowState.color}}
              @text={{t this.remoteHandsRequest.workflowState.labelKey}}
            />
          </h1>
          <div class="flex flex-wrap items-center gap-6 text-sm text-black">
            <span class="flex items-center gap-1.5" data-test-remote-hands-request-details-execution-option>
              <IconsStopwatch class="size-4 text-gray-400" />
              {{t this.remoteHandsRequest.executionOptionLabelKey}}
            </span>
            <span class="flex items-center gap-1.5" data-test-remote-hands-request-details-created>
              <IconsCalendar class="size-4 text-gray-400" />
              {{dateFormat this.remoteHandsRequest.createdat "YYYY-MM-DD HH:mm"}}
            </span>
          </div>
        </div>
        {{#if (canManage)}}
          <Dropdown
            class="w-9 h-8 cursor-pointer flex items-center justify-center rounded-sm bg-white border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800"
            aria-label={{t "label.action-menu-aria-label" name=this.remoteHandsRequest.id}}
            data-test-remote-hands-request-details-menu
            @chevron={{false}}
          >
            <:header>
              <IconsMoreHorizontal class="w-12" />
            </:header>
            <:default>
              <DropdownDestructiveItem
                data-test-remote-hands-request-details-cancel
                disabled={{this.remoteHandsRequest.isNotCancelable}}
                {{on
                  "click"
                  (modal
                    "modals/confirm"
                    task=this.remoteHandsRequest.cancelTask
                    text=(t "remote-hands-requests.cancel.text")
                    titleText=(t "remote-hands-requests.cancel.title" remoteHandsId=this.remoteHandsRequest.id)
                    buttonText=(t "remote-hands-requests.cancel.button")
                  )
                }}
              >
                {{t "button.cancel"}}
              </DropdownDestructiveItem>
            </:default>
          </Dropdown>
        {{/if}}
      </div>
      <div class="flex flex-col gap-8 mt-8">
        <Box data-test-remote-hands-request-details-instructions>
          <:header>{{t "label.instructions"}}</:header>
          <:default>
            <p class="text-sm text-black whitespace-pre-line">{{this.remoteHandsRequest.instructions}}</p>
          </:default>
        </Box>
        <Box>
          <:header>{{t "label.services"}}</:header>
          <:default>
            <Table class="-mx-6 -my-6" @items={{this.services}} @footer={{false}} @outsideBorders={{false}}>
              <:header>
                <TableTh class="w-72">{{t "label.service"}}</TableTh>
                <TableTh>{{t "label.location"}}</TableTh>
              </:header>
              <:body as |serviceKey|>
                <TableTd data-test-remote-hands-request-details-service>
                  {{#if (this.isColocationServer serviceKey)}}
                    <LinkTo
                      data-test-remote-hands-request-details-service-link
                      @route="server.details"
                      @model={{serviceKey}}
                    >{{serviceKey}}</LinkTo>
                  {{else}}
                    {{#let (this.colocationRack serviceKey) as |colocationRack|}}
                      {{#if colocationRack.cabinet}}
                        {{colocationRack.cabinet}}
                        <span class="text-gray-500">({{colocationRack.id}})</span>
                      {{else}}
                        {{serviceKey}}
                      {{/if}}
                    {{/let}}
                  {{/if}}
                </TableTd>
                <TableTd data-test-remote-hands-request-details-service-location>
                  <DataCenter @dataCenter={{this.remoteHandsRequest.datacenter}} />
                </TableTd>
              </:body>
            </Table>
          </:default>
        </Box>
        {{#if this.remoteHandsRequest.shipments.length}}
          <Box data-test-remote-hands-request-details-shipments>
            <:header>{{t "label.shipments"}}</:header>
            <:default>
              <div class="flex flex-col gap-4">
                {{#each this.remoteHandsRequest.shipments as |shipment|}}
                  <EntityShipment @entity={{shipment}} @includeRemoteHandsRequest={{false}} />
                {{/each}}
              </div>
            </:default>
          </Box>
        {{/if}}
      </div>
    </PageContent>
  </template>
}
